/**
 * Search Info Tooltip Component
 * 
 * Displays a hoverable info icon with advanced search syntax hints
 */

import React, { useState } from 'react';
import { Info, X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface SearchInfoTooltipProps {
  className?: string;
}

export function SearchInfoTooltip({ className = '' }: SearchInfoTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const handleMouseEnter = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const tooltipWidth = 512; // max-w-2xl = 512px
      
      // Position tooltip, ensuring it doesn't go off-screen
      let left = rect.left;
      if (left + tooltipWidth > viewportWidth) {
        left = viewportWidth - tooltipWidth - 16; // 16px padding from edge
      }
      if (left < 16) {
        left = 16;
      }
      
      setTooltipPosition({
        top: rect.bottom + 8,
        left: left,
      });
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const tooltipContent = (
    <div
      className={`fixed z-50 bg-white dark:bg-gray-800 border border-theme-border rounded-lg shadow-xl p-6 max-w-2xl ${
        isOpen ? 'block' : 'hidden'
      }`}
      style={
        tooltipPosition
          ? {
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }
          : undefined
      }
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-theme-text">Advanced Search Syntax</h3>
        <button
          onClick={() => setIsOpen(false)}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        >
          <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      <div className="space-y-4 text-sm text-theme-text-secondary">
        {/* AND Operator */}
        <div>
          <h4 className="font-semibold text-theme-text mb-2">AND Operator</h4>
          <p className="mb-2">Find opportunities containing ALL specified terms.</p>
          <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded border border-gray-200 dark:border-gray-700">
            <p className="font-mono text-xs mb-1">contaminated AND sites AND BC</p>
            <p className="font-mono text-xs">remediation assessment</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              (space-separated terms are AND by default)
            </p>
          </div>
        </div>

        {/* OR Operator */}
        <div>
          <h4 className="font-semibold text-theme-text mb-2">OR Operator</h4>
          <p className="mb-2">Find opportunities containing ANY of the specified terms.</p>
          <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded border border-gray-200 dark:border-gray-700">
            <p className="font-mono text-xs mb-1">Vancouver OR Victoria OR Surrey</p>
            <p className="font-mono text-xs">P.Eng OR CSAP OR QPESA</p>
          </div>
        </div>

        {/* NOT Operator */}
        <div>
          <h4 className="font-semibold text-theme-text mb-2">NOT Operator</h4>
          <p className="mb-2">Exclude opportunities containing specific terms.</p>
          <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded border border-gray-200 dark:border-gray-700">
            <p className="font-mono text-xs mb-1">assessment NOT preliminary</p>
            <p className="font-mono text-xs">-consulting</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              (use NOT or - prefix)
            </p>
          </div>
        </div>

        {/* Combining Operators */}
        <div>
          <h4 className="font-semibold text-theme-text mb-2">Combining Operators</h4>
          <p className="mb-2">Use parentheses to group terms.</p>
          <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded border border-gray-200 dark:border-gray-700">
            <p className="font-mono text-xs mb-1">(contaminated OR remediation) AND (BC OR Ontario)</p>
            <p className="font-mono text-xs">assessment AND (Vancouver OR Victoria) NOT preliminary</p>
          </div>
        </div>

        {/* Field Targeting */}
        <div>
          <h4 className="font-semibold text-theme-text mb-2">Field-Targeted Search</h4>
          <p className="mb-2">Search specific fields using field:value syntax.</p>
          <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded border border-gray-200 dark:border-gray-700">
            <p className="font-mono text-xs mb-1">title:assessment</p>
            <p className="font-mono text-xs mb-1">description:remediation</p>
            <p className="font-mono text-xs mb-1">organization:City of Vancouver</p>
            <p className="font-mono text-xs">location:Vancouver</p>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Available fields: title, description, organization, location, city, state_province, 
            region, status, opportunity_type, keywords, tags, and more
          </p>
        </div>

        {/* Complex Examples */}
        <div>
          <h4 className="font-semibold text-theme-text mb-2">Complex Query Examples</h4>
          <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded border border-gray-200 dark:border-gray-700 space-y-1">
            <p className="font-mono text-xs">
              (environmental impact assessment) AND (BC OR Ontario) AND (P.Eng OR CSAP)
            </p>
            <p className="font-mono text-xs">
              contaminated sites AND remediation NOT preliminary assessment
            </p>
            <p className="font-mono text-xs">
              (site assessment OR Phase I) AND Vancouver
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setIsOpen(!isOpen)}
        className={`p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors cursor-help ${className}`}
        aria-label="Advanced search syntax help"
      >
        <Info className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
      </button>

      {isOpen && createPortal(tooltipContent, document.body)}
    </>
  );
}
